import { gql } from '@apollo/client';

import '@cobliteam/common-types';

import type {
  CreateMaintenanceHistoryInput,
  CreateMaintenancePartsInput,
  DeleteMaintenanceHistoryInput,
  DeleteMaintenancePartsInput,
  GetMaintenanceHistoryInput,
  GetMaintenancePartsInput,
  Maintenance,
  MaintenancePartResponse,
  MaintenanceResponse,
  Part,
  UpdateMaintenanceHistoryInput,
  UpdateMaintenancePartsInput,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import type { GraphQLRestDataSourceError } from '../../request/Types';

export class GraphQLMaintenanceRequester {
  public getMaintenanceHistory = (request: GetMaintenanceHistoryInput): Promise<MaintenanceResponse> =>
    graphQLRequester
      .query<{
        maintenanceHistory: MaintenanceResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query maintenanceHistory($input: GetMaintenanceHistoryInput!) {
            maintenanceHistory(input: $input) {
              count
              rows {
                createdBy
                description
                id
                occurrenceAt
                odometerInKms
                totalPrice
                types
                updatedAt
                updatedBy
                recurrences {
                  description
                  everyKms
                }
                parts {
                  description
                  id
                  part {
                    description
                    id
                  }
                  price
                  quantity
                }
                vehicle {
                  id
                  licensePlate
                  groups {
                    id
                    name
                  }
                  branding {
                    brand
                    model
                    year
                  }
                  size
                  tags {
                    name
                  }
                }
              }
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.maintenanceHistory;
      });

  public getMaintenanceParts = (request: GetMaintenancePartsInput): Promise<MaintenancePartResponse> =>
    graphQLRequester
      .query<{
        maintenanceParts: MaintenancePartResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query maintenanceParts($input: GetMaintenancePartsInput!) {
            maintenanceParts(input: $input) {
              count
              rows {
                description
                id
                part {
                  description
                  id
                }
                price
                quantity
              }
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.maintenanceParts;
      });

  public createMaintenanceHistory = (request: CreateMaintenanceHistoryInput): Promise<Maintenance> =>
    graphQLRequester
      .mutate<{
        createMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation createMaintenanceHistory(
            $input: CreateMaintenanceHistoryInput!
          ) {
            createMaintenanceHistory(input: $input) {
              id
              types
              occurrenceAt
              totalPrice
              odometerInKms
              description
              parts {
                id
                description
                part {
                  description
                  id
                }
                price
                quantity
              }
              recurrences {
                description
                everyKms
              }
              vehicle {
                id
                licensePlate
                branding {
                  brand
                  model
                  year
                }
                size
              }
              createdBy
              updatedAt
              updatedBy
              createdBy
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.createMaintenanceHistory;
      });

  public updateMaintenanceHistory = (request: UpdateMaintenanceHistoryInput): Promise<Maintenance> =>
    graphQLRequester
      .mutate<{
        updateMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation updateMaintenanceHistory(
            $input: UpdateMaintenanceHistoryInput!
          ) {
            updateMaintenanceHistory(input: $input) {
              id
              types
              occurrenceAt
              totalPrice
              odometerInKms
              description
              parts {
                id
                description
                price
                quantity
              }
              recurrences {
                description
                everyKms
              }
              updatedAt
              updatedBy
              createdBy
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateMaintenanceHistory;
      });

  public deleteMaintenanceHistory = (request: DeleteMaintenanceHistoryInput): Promise<null> =>
    graphQLRequester
      .mutate<{
        deleteMaintenanceHistory: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation deleteMaintenanceHistory(
            $input: DeleteMaintenanceHistoryInput!
          ) {
            deleteMaintenanceHistory(input: $input)
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return null;
      });

  public createMaintenanceParts = (request: CreateMaintenancePartsInput): Promise<Part> =>
    graphQLRequester
      .mutate<{
        createMaintenanceParts: Part;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation createMaintenanceParts($input: CreateMaintenancePartsInput!) {
            createMaintenanceParts(input: $input) {
              id
              description
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => result.createMaintenanceParts)
      .catch((result) => {
        throw (result.errors as Array<GraphQLRestDataSourceError>)[0];
      });

  public updateMaintenanceParts = (request: UpdateMaintenancePartsInput): Promise<Part> =>
    graphQLRequester
      .mutate<{
        updateMaintenanceParts: Part;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation updateMaintenanceParts($input: UpdateMaintenancePartsInput) {
            updateMaintenanceParts(input: $input) {
              id
              description
            }
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateMaintenanceParts;
      });

  public deleteMaintenanceParts = (request: DeleteMaintenancePartsInput): Promise<null> =>
    graphQLRequester
      .mutate<{
        deleteMaintenanceParts: Maintenance;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation deleteMaintenanceParts($input: DeleteMaintenancePartsInput) {
            deleteMaintenanceParts(input: $input)
          }
        `,
        variables: {
          input: request,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return null;
      });
}

export default GraphQLMaintenanceRequester;
